import {
  GiftItemBrand,
  ThanksPrintConfig,
  ThanksPrintTemplate,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { GiftThanksPrintDto } from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { Member } from '@shared/api/member/member.interface';
import { Gender } from '@shared/types';
import { getReverseType } from '@shared/utils/memberUtils';

/** 감사글 안에서 고인/상중 표현으로 치환되는 토큰 */
export const THANKS_DECEASED_TOKEN = '###';

/** 발신 표현 옵션 (기본: 배상) */
export const THANKS_SENDER_SUFFIXES = ['배상', '드림', '올림'] as const;
export type ThanksSenderSuffix = typeof THANKS_SENDER_SUFFIXES[number];

/** 상주 정보가 없을 때 이름 fallback */
export const THANKS_FALLBACK_NAME = 'OOO';

/**
 * reverseType(고인의 상주에 대한 관계 용어) → 구어체 호칭.
 * 예: 부친→아버지, 모친→어머니, 조부→할아버지, 조모→할머니 ...
 */
const REVERSE_TYPE_TO_WORD: Record<string, string> = {
  부친: '아버지',
  모친: '어머니',
  조부: '할아버지',
  조모: '할머니',
  외조부: '외할아버지',
  외조모: '외할머니',
  시부: '시아버지',
  시모: '시어머니',
  시조부: '시할아버지',
  시조모: '시할머니',
  빙조부: '처조부',
  빙조모: '처조모',
  장인: '장인어른',
  장모: '장모님',
  빙부: '장인어른',
  빙모: '장모님',
  부군: '남편',
  부인: '아내',
  배우자: '배우자',
  형제: '형제',
};

/**
 * ### 표현 생성 — relationType(상주의 고인에 대한 관계) + 고인 성별로 계산.
 * - 관계가 확인되면 "저희 {구어체 호칭} 상중" (예: "저희 아버지 상중")
 * - 관계를 만들 수 없으면 "상중" (저희도 제외)
 * - 상주(relationType 미설정)는 아들(자)로 간주해 부친/모친 계산
 */
export const getThanksDeceasedPhrase = (
  relationType: string | undefined,
  deceasedSex: Gender | undefined,
): string => {
  if (!deceasedSex) return '상중';
  const rel = relationType?.trim() || '자';
  const reverseType = getReverseType(rel, deceasedSex);
  const word = reverseType ? REVERSE_TYPE_TO_WORD[reverseType] ?? reverseType : '';
  return word ? `저희 ${word} 상중` : '상중';
};

/** 상주 이름 (없으면 OOO) */
export const getThanksSenderName = (member?: Pick<Member, 'fullName'> | null): string =>
  member?.fullName?.trim() || THANKS_FALLBACK_NAME;

/** 프리셋의 ### 토큰을 "저희 OOO 상중" 표현으로 치환 */
export const fillThanksMessage = (
  template: string,
  relationType: string | undefined,
  deceasedSex: Gender | undefined,
): string =>
  template
    .split(THANKS_DECEASED_TOKEN)
    .join(getThanksDeceasedPhrase(relationType, deceasedSex));

/** 이름 + 발신표현 합성 ("홍길동 배상") */
export const composeThanksSender = (name: string, suffix: ThanksSenderSuffix): string =>
  `${name.trim() || THANKS_FALLBACK_NAME} ${suffix}`;

/**
 * 지정 문구 비교용 정규화 — 백엔드와 동일한 규칙.
 * 줄바꿈(CRLF→LF)·앞뒤 공백만 관대하게 보고, 내부 들여쓰기는 문구의 일부로 엄격 비교.
 */
export const normalizeThanksPreset = (s: string): string =>
  s.replace(/\r\n/g, '\n').trim();

/**
 * 템플릿의 유효한 지정 문구 세트 (빈 문구 제외).
 * 비어있지 않으면 자유입력 불가 — 이 중 하나만 선택 가능. 빈 배열 = 기존 자유입력.
 */
export const templateMessagePresets = <TImage>(
  template?: ThanksPrintTemplate<TImage> | null,
): string[] =>
  (template?.messagePresets ?? []).filter((p) => normalizeThanksPreset(p).length > 0);

/**
 * 이름 입력에 허용되는 최대 글자수.
 * 서버의 senderNameMaxLength는 전송되는 합성값("홍길동 배상") 전체에 적용되므로,
 * 발신표현(공백 1자 + 표현 2자 = 3자)을 제외한 이름 몫을 돌려준다.
 */
export const thanksSenderNameLimit = <TImage>(
  template?: ThanksPrintTemplate<TImage> | null,
): number | undefined => {
  const max = template?.senderNameMaxLength;
  if (typeof max !== 'number' || max <= 0) return undefined;
  return Math.max(1, max - 3);
};

/** 감사인쇄 가능 브랜드 판정을 위한 최소 주문 라인 형태 */
export interface ThanksPrintLine {
  /** 라인 식별자 — 감사인쇄 대상 상품 수 집계(중복 제거)에 사용 */
  itemId: string;
  giftItemInfo?: {
    giftItemBrand?: GiftItemBrand | string;
    /** 감사카드 사용 여부 오버라이드 (undefined = 브랜드 thanksPrint.enabled 따름) */
    useThanksPrint?: boolean;
    /** 이 상품에서 허용하는 템플릿 _id 목록 (빈 배열/미설정 = 브랜드 전체 허용) */
    thanksPrintTemplates?: string[];
  } | null;
}

/** 주문 전체의 대표 감사인쇄 설정 (주문당 1건으로 통일) */
export interface OrderThanksPrint {
  brandId: string;
  config: ThanksPrintConfig<Image>;
  /** 감사인쇄가 공통 적용되는 대상 상품 수 */
  eligibleCount: number;
  /**
   * 대상 상품들이 공통으로 허용하는 템플릿 _id 목록 (교집합).
   * undefined = 제한 없음(브랜드 전체 허용)
   */
  allowedTemplateIds?: string[];
}

/**
 * 주문 라인들에서 감사인쇄 가능 브랜드를 모아 대표 1개를 고른다.
 * 단건(바로구매)·다건(장바구니) 결제 어디서 호출해도 동일하게 동작하도록 통일한 로직.
 * - 가능 조건: 브랜드가 populate된 객체 + 인쇄 대상(useThanksPrint ?? thanksPrint.enabled)
 *   + 노출 가능 템플릿(enabled !== false ∩ 상품별 허용 목록 교집합) 1개 이상
 * - 대표 선정: 저장된 디자인 브랜드(preferredBrandId) 우선, 없으면 첫 가능 브랜드
 * - eligibleCount: 감사인쇄 대상 상품 수(같은 itemId 중복 제외)
 */
export const resolveOrderThanksPrint = (
  lines: ThanksPrintLine[],
  preferredBrandId?: string,
): OrderThanksPrint | null => {
  const entries: {
    brandId: string;
    config: ThanksPrintConfig<Image>;
    allowed: Set<string> | null; // 상품별 허용 목록의 교집합 (null = 전체 허용)
    itemIds: Set<string>;
  }[] = [];
  for (const line of lines) {
    const brand = line.giftItemInfo?.giftItemBrand;
    if (!brand || typeof brand !== 'object' || !('thanksPrint' in brand)) continue;
    const config = brand.thanksPrint as ThanksPrintConfig<Image> | undefined;
    if (!config) continue;
    // 상품 오버라이드 우선 (백엔드와 동일: useThanksPrint ?? enabled ?? false)
    if (!(line.giftItemInfo?.useThanksPrint ?? config.enabled)) continue;

    let entry = entries.find((c) => c.brandId === brand._id);
    if (!entry) {
      entry = { brandId: brand._id, config, allowed: null, itemIds: new Set() };
      entries.push(entry);
    }
    entry.itemIds.add(line.itemId);

    // 허용 목록 교집합 — 미지정(빈 배열) 상품은 전체 허용이라 교집합에 영향 없음
    const lineAllowed = (line.giftItemInfo?.thanksPrintTemplates ?? [])
      .map(String)
      .filter(Boolean);
    if (lineAllowed.length > 0) {
      const current = entry.allowed;
      entry.allowed =
        current === null
          ? new Set(lineAllowed)
          : new Set(lineAllowed.filter((id) => current.has(id)));
    }
  }

  // 노출 가능 템플릿(enabled ∩ 허용 교집합)이 1개 이상인 브랜드만 후보
  const candidates = entries.filter((c) =>
    (c.config.templates ?? []).some(
      (t) =>
        t.enabled !== false && (c.allowed === null || (t._id && c.allowed.has(t._id))),
    ),
  );
  if (candidates.length === 0) return null;
  const chosen = candidates.find((c) => c.brandId === preferredBrandId) ?? candidates[0]!;

  const eligibleItemIds = new Set<string>();
  candidates.forEach((c) => c.itemIds.forEach((id) => eligibleItemIds.add(id)));

  return {
    brandId: chosen.brandId,
    config: chosen.config,
    eligibleCount: eligibleItemIds.size,
    ...(chosen.allowed !== null && { allowedTemplateIds: Array.from(chosen.allowed) }),
  };
};

/** 주문에 보낼 감사인쇄 선택값 (giftThanksPrintAtom과 호환되는 구조) */
export interface ThanksPrintSelectionInput {
  message: string;
  senderName: string;
  senderSuffix: ThanksSenderSuffix;
  brandId?: string;
  templateId?: string;
}

/**
 * 감사인쇄 선택값 + 주문 대표 브랜드 설정 → 주문 전송용 thanksPrint DTO.
 * - 감사글(message)이 비어있으면(미작성) undefined → 주문에 thanksPrint를 넣지 않음.
 * - templateId는 같은 브랜드의 저장된 디자인을 쓰되, 매칭 안 되면 첫 노출 템플릿으로 보정.
 *   (allowedTemplateIds가 있으면 그 목록 밖 템플릿은 후보에서 제외 — 상품별 허용 제한)
 * - 템플릿에 지정 문구 세트가 있으면 message를 세트 원본으로 정규화해 전송
 *   (세트 밖 문구는 서버가 400 거부 — 보존된 옛 자유입력이 남아있는 경우 첫 문구로 보정)
 * - senderName은 발신표현까지 합성("홍길동 배상")해 미리보기와 동일하게 인쇄되도록 하고,
 *   템플릿 글자수 제한(합성 전체 기준)을 넘지 않게 이름을 잘라 서버 거부를 방지한다.
 */
export const buildThanksPrintDto = (
  selection: ThanksPrintSelectionInput | null,
  config: ThanksPrintConfig<Image>,
  brandId: string,
  allowedTemplateIds?: string[],
): GiftThanksPrintDto | undefined => {
  const inputMessage = selection?.message?.trim();
  if (!selection || !inputMessage) return undefined;

  const allowed =
    allowedTemplateIds && allowedTemplateIds.length > 0
      ? new Set(allowedTemplateIds)
      : null;
  const templates = (config.templates ?? [])
    .filter(
      (t) => t.enabled !== false && (!allowed || (t._id && allowed.has(t._id))),
    )
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const firstTemplate = templates[0];
  if (!firstTemplate) return undefined;

  const matched =
    selection.brandId === brandId && selection.templateId
      ? templates.find((t) => t._id === selection.templateId)
      : undefined;
  const template = matched ?? firstTemplate;
  const templateId = template._id;
  if (!templateId) return undefined;

  // 지정 문구 세트 — 세트 원본 그대로 전송 (내부 들여쓰기·줄바꿈 보존)
  const presets = templateMessagePresets(template);
  const message =
    presets.length > 0
      ? presets.find(
          (p) => normalizeThanksPreset(p) === normalizeThanksPreset(inputMessage),
        ) ?? presets[0]!
      : inputMessage;

  const { name, suffix } = parseThanksSender(selection.senderName, selection.senderSuffix);
  const nameLimit = thanksSenderNameLimit(template);
  const limitedName = nameLimit !== undefined ? name.slice(0, nameLimit) : name;
  return { templateId, message, senderName: composeThanksSender(limitedName, suffix) };
};

/** 유효한 발신표현인지 */
export const isThanksSenderSuffix = (value: unknown): value is ThanksSenderSuffix =>
  THANKS_SENDER_SUFFIXES.includes(value as ThanksSenderSuffix);

/**
 * 저장된 발신자 값을 이름/표현으로 분리·정규화.
 * - suffix가 유효하지 않으면 기본 '배상'
 * - 레거시 합성값("홍길동 배상")처럼 이름에 표현이 붙어있으면 떼어냄
 *   (undefined가 붙는 "홍길동 배상 undefined" 류 깨짐 방지)
 */
export const parseThanksSender = (
  storedName: string | undefined,
  storedSuffix: unknown,
): { name: string; suffix: ThanksSenderSuffix } => {
  let name = (storedName ?? '').trim();
  let suffix = isThanksSenderSuffix(storedSuffix) ? storedSuffix : undefined;
  for (const s of THANKS_SENDER_SUFFIXES) {
    if (name.endsWith(` ${s}`)) {
      name = name.slice(0, name.length - s.length).trim();
      suffix = suffix ?? s;
      break;
    }
  }
  return { name: name || THANKS_FALLBACK_NAME, suffix: suffix ?? '배상' };
};

/** 감사글 입력 제한 */
export const THANKS_MESSAGE_MAX_LINES = 4;
export const THANKS_MESSAGE_MAX_CHARS = 100; // 줄바꿈 제외 글자수

/** 줄바꿈 제외 글자수 */
export const thanksMessageCharCount = (value: string): number =>
  value.replace(/\n/g, '').length;

/**
 * 감사글을 4줄 / 100자(줄바꿈 제외) 이내로 제한.
 * 초과분은 잘라내되 줄바꿈 위치는 보존.
 */
export const clampThanksMessage = (value: string): string => {
  const lines = value.split('\n').slice(0, THANKS_MESSAGE_MAX_LINES);
  let count = 0;
  let result = '';
  for (const ch of lines.join('\n')) {
    if (ch === '\n') {
      result += ch;
      continue;
    }
    if (count >= THANKS_MESSAGE_MAX_CHARS) break;
    result += ch;
    count += 1;
  }
  return result;
};

/**
 * 감사글 프리셋 (### 는 사용 시 member 표현으로 치환).
 * 줄바꿈은 인쇄 레이아웃에 맞춰 유지됨(break-keep + pre-line).
 */
export const THANKS_MESSAGE_PRESETS: string[] = [
  `바쁘신 와중에도 불구하고\n${THANKS_DECEASED_TOKEN}에\n위로해주셔서 감사합니다.`,
  `바쁘신 와중에도 친히\n${THANKS_DECEASED_TOKEN}에\n위로해 주셔서 감사합니다.`,
  `바쁘신 와중에도\n${THANKS_DECEASED_TOKEN}에\n귀한 발걸음 해 주신 덕분에\n큰 힘이 되었습니다.`,
  `조문과 부의로 위로해 주신 것에\n깊은 감사의 말씀을 드립니다.`,
  `${THANKS_DECEASED_TOKEN}에 따뜻한 마음으로\n위로해 주셔서 진심으로 감사합니다.\n덕분에 슬픔을 이겨낼 수 있었습니다.`,
];
